/**
 * Blog listing page with search, category filtering, and a featured-post
 * hero layout. Includes JSON-LD structured data for SEO.
 *
 * @module BlogPage
 */

import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Stack,
  TextField,
  InputAdornment,
  alpha,
  Grid2 as Grid,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import { GREEN, DARK_BLUE } from '../theme';
import { PageTransition, FadeInSection, FloatingOrb, StaggerContainer, StaggerItem } from '../components/AnimatedComponents';
import api from '../api';

/** Shape of a single translation within a blog post. */
interface BlogTranslation {
  title: string;
  excerpt: string;
  content: string;
  meta_description: string;
  meta_keywords: string;
}

/** Complete blog post record as returned by the API. */
interface BlogPost {
  id: string;
  slug: string;
  translations: Record<string, BlogTranslation>;
  author: string;
  category: string;
  tags: string[];
  cover_image: string;
  is_published: boolean;
  reading_time: number;
  created_at: string;
  updated_at: string;
}

/** Resolve the best available translation for a post, falling back to nl then en. */
function getTranslation(post: BlogPost, lang: string): BlogTranslation {
  return post.translations[lang] || post.translations['nl'] || post.translations['en'] || {
    title: '',
    excerpt: '',
    content: '',
    meta_description: '',
    meta_keywords: '',
  };
}

/** Blog listing page with search, category filter, and featured post layout. */
export default function BlogPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.split('-')[0] || 'nl';

  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  useEffect(() => {
    api
      .get('/api/blog/', { params: { published_only: true, lang } })
      .then((res) => setPosts(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [lang]);

  const categories = Array.from(new Set(posts.map((p) => p.category).filter(Boolean)));

  const filteredPosts = posts.filter((p) => {
    const tr = getTranslation(p, lang);
    const matchesSearch =
      !search ||
      tr.title.toLowerCase().includes(search.toLowerCase()) ||
      tr.excerpt.toLowerCase().includes(search.toLowerCase()) ||
      p.tags.some((tag) => tag.toLowerCase().includes(search.toLowerCase()));
    const matchesCategory = !selectedCategory || p.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredPost = filteredPosts[0];
  const remainingPosts = filteredPosts.slice(1);

  // JSON-LD for blog listing (CollectionPage)
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: t('blog.seoTitle'),
    description: t('blog.seoDesc'),
    url: 'https://www.booge-it.com/blog',
    isPartOf: {
      '@type': 'WebSite',
      name: 'Booge IT',
      url: 'https://www.booge-it.com',
    },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredPosts.map((post, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        url: `https://www.booge-it.com/blog/${post.slug}`,
        name: getTranslation(post, lang).title,
      })),
    },
  };

  // BreadcrumbList
  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: t('nav.home'), item: 'https://www.booge-it.com/' },
      { '@type': 'ListItem', position: 2, name: t('blog.title'), item: 'https://www.booge-it.com/blog' },
    ],
  };

  return (
    <PageTransition>
      <SEO
        title={t('blog.seoTitle')}
        description={t('blog.seoDesc')}
        canonical="/blog"
        jsonLd={jsonLd}
        hreflangMap={{ nl: '/blog', en: '/blog', de: '/blog', fr: '/blog', es: '/blog' }}
      />
      {/* Extra structured data for breadcrumbs */}
      <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>

      <Box
        component="section"
        sx={{
          py: { xs: 4, md: 8 },
          position: 'relative',
          overflow: 'hidden',
          minHeight: '80vh',
          background: `linear-gradient(180deg, ${alpha(DARK_BLUE, 0.03)} 0%, transparent 40%)`,
        }}
      >
        <FloatingOrb size={300} color={GREEN} top={-80} right={-100} />
        <FloatingOrb size={200} color={DARK_BLUE} bottom={100} left={-60} animationDelay="3s" />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          {/* Header */}
          <FadeInSection>
            <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  fontWeight: 800,
                  mb: 2,
                  background: `linear-gradient(135deg, ${DARK_BLUE}, ${GREEN})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {t('blog.title')}
              </Typography>
              <Typography
                variant="h5"
                component="p"
                color="text.secondary"
                sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}
              >
                {t('blog.subtitle')}
              </Typography>

              {/* Search */}
              <TextField
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('blog.search')}
                variant="outlined"
                size="small"
                sx={{
                  maxWidth: 500,
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 25,
                    bgcolor: 'white',
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </FadeInSection>

          {/* Category filter */}
          {categories.length > 0 && (
            <FadeInSection delay={0.1}>
              <Stack
                direction="row"
                spacing={1}
                justifyContent="center"
                flexWrap="wrap"
                useFlexGap
                sx={{ mb: 4 }}
                role="navigation"
                aria-label={t('blog.categories')}
              >
                <Chip
                  label={t('blog.allCategories')}
                  onClick={() => setSelectedCategory('')}
                  color={selectedCategory === '' ? 'secondary' : 'default'}
                  variant={selectedCategory === '' ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 600 }}
                />
                {categories.map((cat) => (
                  <Chip
                    key={cat}
                    label={cat}
                    onClick={() => setSelectedCategory(cat)}
                    color={selectedCategory === cat ? 'secondary' : 'default'}
                    variant={selectedCategory === cat ? 'filled' : 'outlined'}
                    sx={{ fontWeight: 500 }}
                  />
                ))}
              </Stack>
            </FadeInSection>
          )}

          {loading ? (
            <Typography textAlign="center" color="text.secondary">
              {t('admin.loading')}
            </Typography>
          ) : filteredPosts.length === 0 ? (
            <FadeInSection>
              <Typography textAlign="center" color="text.secondary" sx={{ py: 8, fontSize: '1.1rem' }}>
                {t('blog.noPosts')}
              </Typography>
            </FadeInSection>
          ) : (
            <>
              {/* Featured post */}
              {featuredPost && (
                <FadeInSection delay={0.15}>
                  <Card
                    component="article"
                    sx={{
                      mb: 6,
                      overflow: 'hidden',
                      border: `1px solid ${alpha(GREEN, 0.1)}`,
                      transition: 'all 0.4s ease',
                      '&:hover': {
                        boxShadow: `0 12px 40px ${alpha(DARK_BLUE, 0.1)}`,
                        borderColor: alpha(GREEN, 0.25),
                      },
                    }}
                  >
                    <Grid container>
                      {featuredPost.cover_image && (
                        <Grid size={{ xs: 12, md: 6 }}>
                          <CardMedia
                            component="img"
                            image={featuredPost.cover_image}
                            alt={getTranslation(featuredPost, lang).title}
                            sx={{ height: { xs: 240, md: '100%' }, objectFit: 'cover' }}
                          />
                        </Grid>
                      )}
                      <Grid size={{ xs: 12, md: featuredPost.cover_image ? 6 : 12 }}>
                        <CardContent sx={{ p: { xs: 3, md: 4 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                            <Chip
                              label={t('blog.featured')}
                              size="small"
                              sx={{
                                bgcolor: alpha(GREEN, 0.1),
                                color: GREEN,
                                fontWeight: 700,
                                fontSize: '0.7rem',
                                textTransform: 'uppercase',
                                letterSpacing: 1,
                              }}
                            />
                            {featuredPost.category && (
                              <Chip label={featuredPost.category} size="small" variant="outlined" />
                            )}
                          </Stack>
                          <Typography
                            variant="h3"
                            component="h2"
                            sx={{ fontWeight: 700, mb: 2, lineHeight: 1.3 }}
                          >
                            <RouterLink
                              to={`/blog/${featuredPost.slug}`}
                              style={{ color: 'inherit', textDecoration: 'none' }}
                            >
                              {getTranslation(featuredPost, lang).title}
                            </RouterLink>
                          </Typography>
                          <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ mb: 3, lineHeight: 1.7 }}
                          >
                            {getTranslation(featuredPost, lang).excerpt}
                          </Typography>
                          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                <time dateTime={featuredPost.created_at}>
                                  {t('blog.publishedOn', {
                                    date: new Date(featuredPost.created_at).toLocaleDateString(lang),
                                  })}
                                </time>
                              </Typography>
                            </Stack>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {t('blog.readingTime', { minutes: featuredPost.reading_time })}
                              </Typography>
                            </Stack>
                          </Stack>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {t('blog.by', { author: featuredPost.author })}
                          </Typography>
                          <Button
                            component={RouterLink}
                            to={`/blog/${featuredPost.slug}`}
                            variant="contained"
                            color="secondary"
                            endIcon={<ArrowForwardIcon />}
                            sx={{ alignSelf: 'flex-start', borderRadius: 25, px: 3 }}
                          >
                            {t('blog.readMore')}
                          </Button>
                        </CardContent>
                      </Grid>
                    </Grid>
                  </Card>
                </FadeInSection>
              )}

              {/* Remaining posts grid */}
              {remainingPosts.length > 0 && (
                <StaggerContainer>
                  <Grid container spacing={3}>
                    {remainingPosts.map((post) => {
                      const tr = getTranslation(post, lang);
                      return (
                        <Grid key={post.id} size={{ xs: 12, sm: 6, md: 4 }}>
                          <StaggerItem>
                            <Card
                              component="article"
                              sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                border: `1px solid ${alpha(DARK_BLUE, 0.06)}`,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  transform: 'translateY(-4px)',
                                  boxShadow: `0 8px 30px ${alpha(DARK_BLUE, 0.1)}`,
                                  borderColor: alpha(GREEN, 0.15),
                                },
                              }}
                            >
                              {post.cover_image && (
                                <CardMedia
                                  component="img"
                                  image={post.cover_image}
                                  alt={tr.title}
                                  sx={{ height: 200, objectFit: 'cover' }}
                                />
                              )}
                              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
                                {post.category && (
                                  <Chip
                                    label={post.category}
                                    size="small"
                                    variant="outlined"
                                    sx={{ alignSelf: 'flex-start', mb: 1.5, fontSize: '0.75rem' }}
                                  />
                                )}
                                <Typography
                                  variant="h5"
                                  component="h2"
                                  sx={{ fontWeight: 600, mb: 1.5, lineHeight: 1.4 }}
                                >
                                  <RouterLink
                                    to={`/blog/${post.slug}`}
                                    style={{ color: 'inherit', textDecoration: 'none' }}
                                  >
                                    {tr.title}
                                  </RouterLink>
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mb: 2, flexGrow: 1, lineHeight: 1.7 }}
                                >
                                  {tr.excerpt}
                                </Typography>
                                <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 'auto' }}>
                                  <Typography variant="caption" color="text.secondary">
                                    <time dateTime={post.created_at}>
                                      {new Date(post.created_at).toLocaleDateString(lang)}
                                    </time>
                                  </Typography>
                                  <Stack direction="row" spacing={0.5} alignItems="center">
                                    <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                    <Typography variant="caption" color="text.secondary">
                                      {t('blog.readingTime', { minutes: post.reading_time })}
                                    </Typography>
                                  </Stack>
                                </Stack>
                                {post.tags.length > 0 && (
                                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                                    {post.tags.slice(0, 3).map((tag) => (
                                      <Chip
                                        key={tag}
                                        label={tag}
                                        size="small"
                                        sx={{
                                          fontSize: '0.7rem',
                                          height: 24,
                                          bgcolor: alpha(DARK_BLUE, 0.04),
                                        }}
                                      />
                                    ))}
                                  </Stack>
                                )}
                              </CardContent>
                            </Card>
                          </StaggerItem>
                        </Grid>
                      );
                    })}
                  </Grid>
                </StaggerContainer>
              )}
            </>
          )}
        </Container>
      </Box>
    </PageTransition>
  );
}
